import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Clipboard, Crown, Link2, MessageCircle, Pause, Play, Send, Share2, Users, Video, X } from 'lucide-react';
import { Button } from './components/ui/Button';
import { Input } from './components/ui/Input';
import { ChatMessageText } from './components/chat/ChatMessageText';
import { HostPasswordControl } from './components/room/HostPasswordControl';
import { VideoPlayer } from './components/video/VideoPlayer';
import { createChatEvent, createJoinEvent, createPauseEvent, createPlayEvent, createRotatePasswordEvent, createSeekEvent, createVideoChangeEvent } from './lib/room/roomEvents';
import { RoomClient } from './lib/room/roomClient';
import { parseCreateRoomResponse } from './lib/room/createRoomApi';
import { SyncEngine } from './lib/sync/SyncEngine';
import { validateMessage, validateNickname, validateVideoUrl } from './lib/security/validation';
import { getVideoCapabilities } from './lib/video/playerCapabilities';
import type { ChatMessage } from './types/chat';
import type { VideoPlayerRef, PlayerState } from './types/video';
import type { ConnectionStatus, RoomStateModel } from './types/room';
import type { ServerEvent } from './types/websocket';

interface NavigationState { created?: boolean }

export function App() {
  return <RoutesFallback />;
}

function RoutesFallback() {
  const path = useLocation().pathname;
  if (path.startsWith('/room/')) return <RoomPage />;
  return <HomePage />;
}

function HomePage() {
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState('');
  const [roomUrl, setRoomUrl] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = validateVideoUrl(videoUrl);
    if (!parsed.valid || !parsed.source) {
      setError(parsed.error ?? 'Informe um link de vídeo compatível.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await fetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed.source),
      });
      const { roomId, password } = await parseCreateRoomResponse(response);

      sessionStorage.setItem(`watch-together:password:${roomId}`, password);
      sessionStorage.setItem(`watch-together:host-secret:${roomId}`, password);
      navigate(`/room/${roomId}`, { state: { created: true } satisfies NavigationState });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível criar a sala.');
    } finally {
      setBusy(false);
    }
  };


  const handleEnter = (event: React.FormEvent) => {
    event.preventDefault();
    const match = roomUrl.trim().match(/(?:\/room\/)?([A-Za-z0-9]{12,20})$/);
    if (!match) { setError('Use um link de sala válido.'); return; }
    navigate(`/room/${match[1]}`);
  };

  return (
    <main className="app-shell">
      <div className="page-width">
        <header className="topbar">
          <Link to="/" className="brand"><span className="brand-mark"><Video size={18} /></span><span>Watch Together</span></Link>
          <span className="brand-subtle">salas temporárias, sem contas</span>
        </header>
        <section className="home-main">
          <div className="home-copy">
            <span className="eyebrow">Assista em companhia</span>
            <h1>Assista junto com seus amigos.</h1>
            <p>Crie uma sala, compartilhe o link e tente reproduzir qualquer URL HTTPS com o melhor player disponível.</p>
            <div className="feature-row"><span><span className="feature-dot" />Sem cadastro</span><span><span className="feature-dot" />Chat em tempo real</span><span><span className="feature-dot" />Sala temporária</span></div>
          </div>
          <div className="home-forms">
            <form className="form-panel" onSubmit={handleCreate}>
              <h2>Começar uma sala</h2>
              <p>Escolha a fonte de vídeo e convide quem vai assistir com você.</p>
              <div className="field"><label htmlFor="video-url">URL do vídeo</label><Input id="video-url" value={videoUrl} onChange={(event) => setVideoUrl(event.target.value)} placeholder="Cole qualquer URL HTTPS" autoComplete="off" /><span className="field-help">Links conhecidos usam player controlável; os demais são tentados em um iframe.</span></div>
              {error && <p className="error-text" role="alert">{error}</p>}
              <div className="form-actions"><Button type="submit" disabled={busy}>{busy ? 'Criando...' : 'Criar sala'} <Link2 size={17} /></Button></div>
            </form>
            <form className="form-panel" onSubmit={handleEnter} style={{ marginTop: 12 }}>
              <h2>Já tem um convite?</h2>
              <p>Entre com o link da sala e o código recebido.</p>
              <div className="field"><label htmlFor="room-url">Link da sala</label><Input id="room-url" value={roomUrl} onChange={(event) => setRoomUrl(event.target.value)} placeholder="https://.../room/..." /></div>
              <div className="form-actions"><Button type="submit" variant="secondary">Entrar na sala <Users size={17} /></Button></div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}

function RoomPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const roomId = location.pathname.match(/^\/room\/([^/]+)/)?.[1] ?? '';
  const navigationState = (location.state as NavigationState | null) ?? {};
  const [credentials, setCredentials] = useState<{ nickname: string; password: string } | null>(() => readStoredCredentials(roomId));
  const [currentUserId, setCurrentUserId] = useState('');
  const [nicknameInput, setNicknameInput] = useState(() => readStoredCredentials(roomId)?.nickname ?? '');
  const [passwordInput, setPasswordInput] = useState(() => sessionStorage.getItem(`watch-together:password:${roomId}`) ?? '');
  const [joinError, setJoinError] = useState('');
  const [room, setRoom] = useState<RoomStateModel | null>(null);
  const [chat, setChat] = useState<{ messages: ChatMessage[]; draft: string }>({ messages: [], draft: '' });
  const [connection, setConnection] = useState<{ status: ConnectionStatus; error: string | null }>({ status: 'disconnected', error: null });
  const [videoInput, setVideoInput] = useState('');
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoError, setVideoError] = useState('');
  const [copyLabel, setCopyLabel] = useState('Copiar link');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [hostSecret, setHostSecret] = useState(() => sessionStorage.getItem(`watch-together:host-secret:${roomId}`) ?? '');
  const [passwordRevealed, setPasswordRevealed] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const [duration, setDuration] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const playerRef = useRef<VideoPlayerRef>(null);
  const credentialsRef = useRef(credentials);
  credentialsRef.current = credentials;
  const clientRef = useRef<RoomClient | null>(null);
  const receivedStateRef = useRef(false);
  const suppressLocalPlayerEventsRef = useRef(false);
  const suppressLocalPlayerEventsTimerRef = useRef<number | null>(null);
  const hostIdRef = useRef('');
  const selfUserIdRef = useRef('');

  const syncEngine = useMemo(() => new SyncEngine({
    getPlayerCurrentTime: () => playerRef.current?.getCurrentTime() ?? 0,
    getPlayerState: () => playerRef.current?.getState() ?? 'unstarted',
    getPlayerCapabilities: () => playerRef.current?.capabilities ?? { canPlay: false, canPause: false, canSeek: false, canGetPosition: false, canSync: false },
    onSeekRequired: (position) => { void playerRef.current?.seekTo(position).catch(() => undefined); },
    onPlayRequired: () => { void playerRef.current?.play().catch(() => undefined); },
    onPauseRequired: () => { void playerRef.current?.pause().catch(() => undefined); },
    onLocalCommand: (type, position, clientRevision) => {
      if (type === 'PLAY') clientRef.current?.send(createPlayEvent(position, clientRevision));
      if (type === 'PAUSE') clientRef.current?.send(createPauseEvent(position, clientRevision));
      if (type === 'SEEK') clientRef.current?.send(createSeekEvent(position, clientRevision));
    },
    onLocalVideoChange: (source, clientRevision) => clientRef.current?.send(createVideoChangeEvent(source, clientRevision)),
  }), []);

  const isHost = Boolean(room && currentUserId && room.hostId === currentUserId);

  const handleServerMessage = useCallback((message: ServerEvent) => {
    switch (message.type) {
      case 'JOIN_SUCCESS':
        hostIdRef.current = message.hostId;
        selfUserIdRef.current = message.userId;
        setCurrentUserId(message.userId);
        setConnection((current) => ({ ...current, error: null }));
        break;
      case 'ROOM_STATE': {
        receivedStateRef.current = true;
        suppressLocalPlayerEventsRef.current = true;
        if (suppressLocalPlayerEventsTimerRef.current !== null) window.clearTimeout(suppressLocalPlayerEventsTimerRef.current);
        syncEngine.applyServerMessage(message);
        setRoom({ roomId, hostId: hostIdRef.current, video: message.state, participants: message.participants });
        setChat({ messages: message.chatHistory.map((item) => ({ ...item, isOwn: item.userId === selfUserIdRef.current })), draft: '' });
        if (playerRef.current?.isReady()) {
          suppressLocalPlayerEventsTimerRef.current = window.setTimeout(() => {
            suppressLocalPlayerEventsRef.current = false;
            suppressLocalPlayerEventsTimerRef.current = null;
          }, 1_000);
        }
        setJoinError('');
        break;
      }
      case 'PLAY':
      case 'PAUSE':
      case 'SEEK':
        syncEngine.applyServerMessage(message);
        setRoom((current) => current ? { ...current, video: message.state } : current);
        break;
      case 'VIDEO_CHANGE':
        suppressLocalPlayerEventsRef.current = true;
        if (suppressLocalPlayerEventsTimerRef.current !== null) window.clearTimeout(suppressLocalPlayerEventsTimerRef.current);
        syncEngine.applyServerMessage(message);
        setRoom((current) => current ? { ...current, video: message.state } : current);
        break;
      case 'USER_JOIN':
        setRoom((current) => current ? { ...current, participants: [...current.participants.filter((item) => item.userId !== message.userId), { userId: message.userId, nickname: message.nickname, isHost: message.userId === current.hostId }] } : current);
        break;
      case 'USER_LEAVE':
        setRoom((current) => current ? { ...current, participants: current.participants.filter((item) => item.userId !== message.userId) } : current);
        break;
      case 'HOST_CHANGED':
        hostIdRef.current = message.newHostId;
        setRoom((current) => current ? { ...current, hostId: message.newHostId, participants: current.participants.map((item) => ({ ...item, isHost: item.userId === message.newHostId })) } : current);
        break;
      case 'PASSWORD_ROTATED': {
        if (hostIdRef.current !== selfUserIdRef.current) break;
        const currentCredentials = credentialsRef.current;
        if (currentCredentials) {
          const updatedCredentials = { ...currentCredentials, password: message.password };
          sessionStorage.setItem(`watch-together:credentials:${roomId}`, JSON.stringify(updatedCredentials));
          sessionStorage.setItem(`watch-together:password:${roomId}`, message.password);
          credentialsRef.current = updatedCredentials;
          setCredentials(updatedCredentials);
        }
        sessionStorage.setItem(`watch-together:host-secret:${roomId}`, message.password);
        setHostSecret(message.password);
        setPasswordRevealed(false);
        setShareMessage('Nova senha gerada. Compartilhe-a separadamente do link.');
        break;
      }
      case 'CHAT_MESSAGE':
        setChat((current) => ({ ...current, messages: [...current.messages.slice(-49), { ...message, isOwn: message.userId === selfUserIdRef.current }] }));
        break;
      case 'ERROR':
        setConnection((current) => ({ ...current, error: message.message }));
        if (message.code === 'PASSWORD_CHANGED') {
          sessionStorage.removeItem(`watch-together:credentials:${roomId}`);
          setPasswordInput('');
          setJoinError(message.message);
          setCredentials(null);
          break;
        }
        if (message.code === 'WRONG_PASSWORD' || message.code === 'PASSWORD_LOCKED' || message.code === 'ROOM_NOT_FOUND' || message.code === 'ROOM_FULL') {
          sessionStorage.removeItem(`watch-together:credentials:${roomId}`);
          setJoinError(message.message);
          setCredentials(null);
        }
        break;
      case 'PONG':
        break;
    }
  }, [roomId, syncEngine]);

  useEffect(() => {
    if (!credentials) return undefined;
    const client = new RoomClient({
      roomId,
      onMessage: handleServerMessage,
      onStatusChange: (status) => setConnection((current) => ({ ...current, status, error: status === 'connected' ? null : current.error })),
    });
    clientRef.current = client;
    client.connect(createJoinEvent({ roomId, ...credentials }));
    return () => {
      client.disconnect();
      clientRef.current = null;
      syncEngine.stop();
      if (suppressLocalPlayerEventsTimerRef.current !== null) window.clearTimeout(suppressLocalPlayerEventsTimerRef.current);
    };
  }, [credentials, handleServerMessage, roomId, syncEngine]);

  useEffect(() => {
    if (!credentials) return undefined;
    syncEngine.start();
    const timer = window.setInterval(() => {
      setPlayhead(playerRef.current?.getCurrentTime() ?? 0);
      setDuration(playerRef.current?.getDuration() ?? 0);
    }, 500);
    return () => window.clearInterval(timer);
  }, [credentials, syncEngine]);

  const submitJoin = async (event: React.FormEvent) => {
    event.preventDefault();
    const nickname = validateNickname(nicknameInput);
    if (!nickname.valid) { setJoinError(nickname.error ?? 'Escolha um nickname.'); return; }
    if (!passwordInput.trim()) { setJoinError('Informe o código da sala.'); return; }
    setJoinError('');
    try {
      const response = await fetch(`/api/rooms/${roomId}/exists`);
      if (!response.ok || !(await response.json() as { exists: boolean }).exists) {
        setJoinError('Sala não encontrada ou expirada.');
        return;
      }
    } catch {
      setJoinError('Não foi possível verificar a sala. Tente novamente.');
      return;
    }
    const nextCredentials = { nickname: nicknameInput.trim(), password: passwordInput.trim() };
    sessionStorage.setItem(`watch-together:credentials:${roomId}`, JSON.stringify(nextCredentials));
    setCredentials(nextCredentials);
  };

  const sendPlayback = useCallback((type: 'PLAY' | 'PAUSE' | 'SEEK', position: number) => {
    syncEngine.requestLocalCommand(type, position);
  }, [syncEngine]);

  const controlPlayback = useCallback((type: 'PLAY' | 'PAUSE') => {
    const capabilities = playerRef.current?.capabilities;
    if (type === 'PLAY' && !capabilities?.canPlay) return;
    if (type === 'PAUSE' && !capabilities?.canPause) return;
    const position = playerRef.current?.getCurrentTime() ?? 0;
    sendPlayback(type, position);
  }, [sendPlayback]);

  const handlePlayerState = useCallback((state: PlayerState, position: number) => {
    const capabilities = playerRef.current?.capabilities;
    if (!isHost || !capabilities?.canSync || suppressLocalPlayerEventsRef.current || syncEngine.isRemoteAction(state) || !receivedStateRef.current) return;
    const type = state === 'playing' ? 'PLAY' : state === 'paused' ? 'PAUSE' : null;
    if (!type) return;
    if (type === 'PLAY' && !capabilities.canPlay) return;
    if (type === 'PAUSE' && !capabilities.canPause) return;
    const canonicalState = syncEngine.getCanonicalState();
    if (type === 'PLAY' && canonicalState?.playing) return;
    if (type === 'PAUSE' && canonicalState && !canonicalState.playing) return;
    sendPlayback(type, position);
  }, [isHost, sendPlayback, syncEngine]);

  const handlePlayerReady = useCallback(() => {
    setDuration(playerRef.current?.getDuration() ?? 0);
    syncEngine.synchronizeNow();
    if (suppressLocalPlayerEventsTimerRef.current !== null) window.clearTimeout(suppressLocalPlayerEventsTimerRef.current);
    suppressLocalPlayerEventsTimerRef.current = window.setTimeout(() => {
      suppressLocalPlayerEventsRef.current = false;
      suppressLocalPlayerEventsTimerRef.current = null;
    }, 1_000);
  }, [syncEngine]);

  const handlePlayerSeek = useCallback((position: number) => {
    const expectedPosition = syncEngine.getExpectedPosition();
    const isCanonicalPosition = Math.abs(position - expectedPosition) < 0.5;
    if (isHost && playerRef.current?.capabilities.canSeek && !isCanonicalPosition && !suppressLocalPlayerEventsRef.current && !syncEngine.isRemoteAction()) sendPlayback('SEEK', position);
  }, [isHost, sendPlayback, syncEngine]);

  const handlePlayerError = useCallback((message: string) => {
    setConnection((current) => ({ ...current, error: message }));
  }, []);

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isHost || !playerRef.current?.capabilities.canSeek) return;
    const position = Number(event.target.value);
    sendPlayback('SEEK', position);
  };

  const sendChat = (event: React.FormEvent) => {
    event.preventDefault();
    const validation = validateMessage(chat.draft);
    if (!validation.valid) return;
    clientRef.current?.send(createChatEvent(chat.draft.trim()));
    setChat((current) => ({ ...current, draft: '' }));
  };

  const changeVideo = (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = validateVideoUrl(videoInput);
    if (!parsed.valid || !parsed.source) { setVideoError(parsed.error ?? 'URL de vídeo inválida.'); return; }
    syncEngine.requestVideoChange(parsed.source);
    setVideoDialogOpen(false);
    setVideoInput('');
    setVideoError('');
  };

  const roomUrl = `${window.location.origin}/room/${roomId}`;

  const copyRoomLink = async () => {
    if (!credentials) return false;
    try {
      await navigator.clipboard.writeText(roomUrl);
      setCopyLabel('Link copiado.');
      setShareMessage('Link copiado.');
    } catch {
      setConnection((current) => ({ ...current, error: 'Não foi possível copiar o link neste navegador.' }));
      return false;
    }
    window.setTimeout(() => setCopyLabel('Copiar link'), 2_000);
    return true;
  };

  const copyRoomCode = async () => {
    if (!credentials) return;
    try {
      await navigator.clipboard.writeText(roomId);
      setShareMessage('Código copiado.');
    } catch {
      setShareMessage('Não foi possível copiar o código neste navegador.');
    }
  };

  const shareRoom = async () => {
    if (!credentials) return;
    const share = getNativeShare();
    if (!share) {
      setShareMessage('Compartilhamento nativo indisponível. Copie o link para enviar.');
      setShareModalOpen(true);
      return;
    }
    try {
      await share.call(navigator, { title: 'Watch Together', text: 'Entre na minha sala do Watch Together.', url: roomUrl });
      setShareMessage('Link compartilhado.');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      setShareMessage('Não foi possível abrir o compartilhamento nativo.');
    }
  };

  const shareFromModal = async () => {
    if (getNativeShare()) {
      await shareRoom();
      return;
    }
    if (await copyRoomLink()) setShareMessage('Link copiado para compartilhar.');
  };

  const requestPasswordRotation = () => {
    clientRef.current?.send(createRotatePasswordEvent());
  };

  if (!roomId || !/^[A-Za-z0-9]{12,20}$/.test(roomId)) return <RoomError title="Sala inválida" detail="O link desta sala não parece correto." onBack={() => navigate('/')} />;
  if (!credentials) return <JoinRoom roomId={roomId} nickname={nicknameInput} password={passwordInput} error={joinError} onNickname={setNicknameInput} onPassword={setPasswordInput} onSubmit={submitJoin} created={Boolean(navigationState.created)} />;
  if (!room) return <RoomConnecting roomId={roomId} status={connection.status} error={connection.error} onRetry={() => clientRef.current?.retryNow()} />;

  const playerSource = room.video.source;
  const playerCapabilities = getVideoCapabilities(playerSource);
  return (
    <main className="app-shell room-page">
      <div className="page-width">
        <header className="room-bar">
          <div className="room-title"><Link to="/" className="brand"><span className="brand-mark"><Video size={17} /></span></Link><div><h1>Sala Watch Together</h1><span className="room-id">/{roomId}</span></div></div>
          <span className={`connection-status status-${connection.status}`}><span className="status-dot" />{connection.status === 'connected' ? 'Conectado' : connection.status === 'reconnecting' ? 'Reconectando...' : connection.status === 'connecting' ? 'Conectando...' : 'Offline'}</span>
        </header>
        <div className="room-grid">
          <section className="watch-column">
            <VideoPlayer ref={playerRef} source={playerSource} isHost={isHost} onReady={handlePlayerReady} onStateChange={handlePlayerState} onSeek={handlePlayerSeek} onError={handlePlayerError} />
            <div className="watch-toolbar">
              <div className="toolbar-group">
                {isHost && <>
                  {playerCapabilities.canPlay && <Button variant="secondary" onClick={() => controlPlayback('PLAY')} aria-label="Reproduzir vídeo"><Play size={16} /> Reproduzir</Button>}
                  {playerCapabilities.canPause && <Button variant="secondary" onClick={() => controlPlayback('PAUSE')} aria-label="Pausar vídeo"><Pause size={16} /> Pausar</Button>}
                  <Button variant="ghost" onClick={() => setVideoDialogOpen(true)} aria-label="Trocar vídeo"><X size={16} /> Trocar vídeo</Button>
                </>}
              </div>
              <span className="room-id">{isHost ? 'Você é o host' : `Host: ${room.participants.find((item) => item.userId === room.hostId)?.nickname ?? 'convidado'}`}</span>
            </div>
            {playerCapabilities.canSeek && playerCapabilities.canGetPosition && <div className="seek-row"><span>0:00</span><input type="range" min="0" max={Math.max(duration, 1)} step="0.1" value={Math.min(playhead, Math.max(duration, 1))} onChange={handleSeek} disabled={!isHost || !duration} aria-label="Linha do tempo do vídeo" /><span>{formatSeconds(duration)}</span></div>}
            <div className="info-strip"><span className="info-chip"><Users size={14} /> {room.participants.length} {room.participants.length === 1 ? 'pessoa' : 'pessoas'}</span><span className="info-chip"><MessageCircle size={14} /> Chat ao vivo</span><span className="info-chip">Rev. {room.video.revision}</span></div>
            <div className="invite-panel"><div className="invite-copy"><strong>Convide sua companhia</strong><span>A senha nunca é incluída automaticamente no link.</span></div><div className="invite-actions"><Button variant="secondary" onClick={copyRoomLink}><Clipboard size={16} /> {copyLabel}</Button><Button variant="ghost" className="icon-button" onClick={() => { void shareRoom(); }} aria-label="Compartilhar sala" title="Compartilhar sala"><Share2 size={18} /></Button></div></div>
            {isHost && <section className="host-password-panel">
              <div><strong>Senha da sala</strong><span>Visível apenas para o Host. Não é incluída no link.</span></div>
              <HostPasswordControl password={hostSecret} revealed={passwordRevealed} onToggle={() => setPasswordRevealed((value) => !value)} onGenerate={requestPasswordRotation} />
            </section>}
            {shareMessage && <p className="share-feedback" role="status">{shareMessage}</p>}
            {connection.error && <p className="error-text" role="status">{connection.error}</p>}
          </section>
          <aside className={`chat-panel${chatOpen ? ' chat-open' : ''}`} aria-label="Chat da sala">
            <div className="panel-heading"><h2>Chat</h2><small>{room.participants.length} online</small><button type="button" className="chat-close" aria-label="Fechar chat" onClick={() => setChatOpen(false)}><X size={18} /></button></div>
            <div className="participant-list">{room.participants.map((participant) => <span key={participant.userId} className={`participant ${participant.isHost ? 'host' : ''}`} title={participant.isHost ? 'Host da sala' : undefined}>{participant.isHost && <Crown size={12} />}{participant.nickname}</span>)}</div>
            <div className="chat-messages" aria-live="polite">{chat.messages.length === 0 ? <p className="empty-chat">Ainda não há mensagens.<br />Diga oi para a sala.</p> : chat.messages.map((message) => <div className="chat-message" key={message.id}><div className="chat-meta"><strong>{message.isOwn ? 'Você' : message.nickname}</strong><span>{formatTime(message.timestamp)}</span></div><div className="chat-bubble"><ChatMessageText>{message.message}</ChatMessageText></div></div>)}</div>
            <form className="chat-compose" onSubmit={sendChat}><Input value={chat.draft} onChange={(event) => setChat((current) => ({ ...current, draft: event.target.value }))} placeholder="Escreva uma mensagem" maxLength={500} aria-label="Mensagem" /><Button type="submit" aria-label="Enviar mensagem"><Send size={16} /></Button></form>
          </aside>
        </div>
      </div>
      {chatOpen && <button type="button" className="chat-scrim" aria-label="Fechar chat" onClick={() => setChatOpen(false)} />}
      <Button className="chat-toggle" variant="secondary" onClick={() => setChatOpen((open) => !open)} aria-label={chatOpen ? 'Fechar chat' : 'Abrir chat'}><MessageCircle size={18} /> Chat <span>{room.participants.length}</span></Button>
      {videoDialogOpen && <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setVideoDialogOpen(false); }}><form className="modal" role="dialog" aria-modal="true" aria-labelledby="change-video-title" onSubmit={changeVideo}><h2 id="change-video-title">Trocar vídeo</h2><p>Todos na sala receberão o novo vídeo e começarão pausados no início.</p><div className="field"><label htmlFor="new-video">URL do vídeo</label><Input id="new-video" value={videoInput} onChange={(event) => setVideoInput(event.target.value)} placeholder="Cole qualquer URL HTTPS" autoFocus /></div>{videoError && <p className="error-text" role="alert">{videoError}</p>}<div className="form-actions"><Button type="button" variant="ghost" onClick={() => setVideoDialogOpen(false)}>Cancelar</Button><Button type="submit">Trocar vídeo</Button></div></form></div>}
      {shareModalOpen && <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setShareModalOpen(false); }}><div className="modal share-modal" role="dialog" aria-modal="true" aria-labelledby="share-room-title">
        <h2 id="share-room-title">Compartilhar sala</h2>
        <div className="field"><label htmlFor="share-room-url">Link da sala</label><div className="copy-field"><Input id="share-room-url" value={roomUrl} readOnly /><Button type="button" variant="secondary" onClick={() => { void copyRoomLink(); }}><Clipboard size={15} /> Copiar link</Button></div></div>
        <div className="field"><label htmlFor="share-room-code">Código de entrada</label><div className="copy-field"><Input id="share-room-code" value={roomId} readOnly /><Button type="button" variant="secondary" onClick={() => { void copyRoomCode(); }}><Clipboard size={15} /> Copiar código</Button></div></div>
        {isHost && <div className="field"><label>Senha da sala</label><HostPasswordControl password={hostSecret} revealed={passwordRevealed} onToggle={() => setPasswordRevealed((value) => !value)} onGenerate={requestPasswordRotation} /></div>}
        {shareMessage && <p className="share-feedback" role="status">{shareMessage}</p>}
        <div className="form-actions"><Button type="button" variant="ghost" onClick={() => setShareModalOpen(false)}>Fechar</Button><Button type="button" onClick={() => { void shareFromModal(); }}>Compartilhar <Share2 size={16} /></Button></div>
      </div></div>}
    </main>
  );
}

function JoinRoom({ roomId, nickname, password, error, created, onNickname, onPassword, onSubmit }: { roomId: string; nickname: string; password: string; error: string; created: boolean; onNickname: (value: string) => void; onPassword: (value: string) => void; onSubmit: (event: React.FormEvent) => void }) {
  return <main className="app-shell"><div className="page-width"><header className="topbar"><Link to="/" className="brand"><span className="brand-mark"><Video size={18} /></span><span>Watch Together</span></Link></header><div className="join-wrap"><form className="form-panel join-panel" onSubmit={onSubmit}><span className="eyebrow">Sala temporária</span><h2 style={{ marginTop: 10 }}>Entrar na sala</h2><p>Escolha como você quer aparecer para as outras pessoas.</p><div className="field"><label htmlFor="nickname">Nickname</label><Input id="nickname" value={nickname} onChange={(event) => onNickname(event.target.value)} placeholder="Como devemos chamar você?" maxLength={24} autoFocus /></div><div className="field"><label htmlFor="password">Senha da sala</label><Input id="password" type="password" autoComplete="current-password" value={password} onChange={(event) => onPassword(event.target.value.toUpperCase())} placeholder="Informe a senha recebida" maxLength={100} /><span className="field-help">Peça a senha ao Host. Ela não faz parte do link nem do código de entrada.</span></div>{created && <p className="success-text">Sala criada. Compartilhe o link e a senha separadamente.</p>}{error && <p className="error-text" role="alert">{error}</p>}<div className="form-actions"><Button type="submit">Entrar <Users size={17} /></Button></div><p className="room-id" style={{ marginTop: 18 }}>Código de entrada: {roomId}</p></form></div></div></main>;
}

function RoomConnecting({ roomId, status, error, onRetry }: { roomId: string; status: ConnectionStatus; error: string | null; onRetry: () => void }) {
  const label = status === 'reconnecting' ? 'Reconectando...' : status === 'connected' ? 'Sincronizando sala...' : status === 'disconnected' ? 'Conexão interrompida' : 'Conectando...';
  return <main className="app-shell"><div className="page-width"><header className="topbar"><Link to="/" className="brand"><span className="brand-mark"><Video size={18} /></span><span>Watch Together</span></Link></header><div className="join-wrap"><div className="room-error"><span className={`connection-status status-${status}`}><span className="status-dot" />{label}</span><h1 style={{ marginTop: 18 }}>Preparando a sala</h1><p>{error ?? 'Estamos recebendo o estado atual do vídeo e da presença.'}</p><p className="room-id">/{roomId}</p>{status === 'disconnected' && <Button onClick={onRetry}>Tentar novamente</Button>}</div></div></div></main>;
}

function RoomError({ title, detail, onBack }: { title: string; detail: string; onBack: () => void }) {
  return <main className="app-shell"><div className="page-width"><header className="topbar"><Link to="/" className="brand"><span className="brand-mark"><Video size={18} /></span><span>Watch Together</span></Link></header><div className="join-wrap"><div className="room-error"><h1>{title}</h1><p>{detail}</p><Button onClick={onBack}>Voltar ao início</Button></div></div></div></main>;
}

function formatSeconds(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0:00';
  const seconds = Math.floor(value);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function getNativeShare(): ((data: ShareData) => Promise<void>) | undefined {
  const share = (navigator as Navigator & { share?: (data: ShareData) => Promise<void> }).share;
  return typeof share === 'function' ? share : undefined;
}

function readStoredCredentials(roomId: string): { nickname: string; password: string } | null {
  try {
    const raw = sessionStorage.getItem(`watch-together:credentials:${roomId}`);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<{ nickname: string; password: string }>;
    if (typeof value.nickname === 'string' && typeof value.password === 'string') {
      return { nickname: value.nickname, password: value.password };
    }
  } catch {
    sessionStorage.removeItem(`watch-together:credentials:${roomId}`);
  }
  return null;
}
